<template>
  <div>
    <div class="my-4">
      <month-pager
        v-model="yearMonth"
        @update:modelValue="pageClick"
      />
    </div>

    <v-progress-linear v-if="isFetching" indeterminate color="primary" class="my-4" />

    <div 
      class="class-block"
      v-for="cwu in classesWithUsages"
      :key="cwu.class.classId"
    >
      <div class="class-block__header">
        <div class="font-weight-bold"> {{ cwu.class.className }} </div>
        <v-spacer />
        <v-btn
          color="primary"
          variant="text"
          :to="`/school-service/usage/${cwu.class.classId}`"
        >月間の利用確認</v-btn>
      </div>

      <div class="table-container mb-4">
        <div class="usages-headers">
          <div class="usages-header col-name">氏名</div>
          <div
            v-for="service in schoolServices"
            :key="service.uniqueServiceId"
            class="usages-header col-item"
          >{{ service.uniqueServiceName }}</div>
        </div>
        
        <div
          v-for="usage in cwu.usages"
          :key="usage.child.childId"
          class="usages-row"
          :style="{ width: `${tableWidth}px` }"
        >
          <div class="usages-item col-name"> {{ usage.child.lastName }} {{ usage.child.firstName }} </div>
          <div
            v-for="service in usage.services"
            :key="service.service.uniqueServiceId"
            class="usages-item col-item"
            @click="toggleUsage(service, usage.child)"
          >
            <v-icon v-if="service.hasUsed" color="primary" size="x-large">mdi-checkbox-marked</v-icon>
            <v-icon v-else color="#c8c8c8" size="x-large">mdi-checkbox-blank-outline</v-icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import MonthPager from '@/components/atoms/MonthPager.vue';
import dayjs from 'dayjs';

import { schoolServiceUsageModule } from '@/store/viewModules/school-service/schoolServiceUsageModule';
import { classModule } from '@/store/dataModules/classModule';
import { NurseryChildResponse, NurseryClassResponse, UniqueServiceMastResponse } from 'chaild-api/lib';
import { schoolServiceModule } from '@/store/dataModules/schoolServiceModule';

interface DataType {
  yearMonth: string;
  isFetching: boolean;
}

interface ClassWithUsages {
  class: NurseryClassResponse;
  usages: ChildWithPerUseService[];
}

interface ChildWithPerUseService {
  child: NurseryChildResponse;
  services: PerUseServiceWithUsage[];
}

interface PerUseServiceWithUsage {
  service: UniqueServiceMastResponse;
  hasUsed: boolean;
  registrationId?: string | number | null;
}

export default defineComponent({
  components: {
    MonthPager,
  },

  data: (): DataType => ({
    yearMonth: dayjs().format('YYYY-MM'),
    isFetching: false,
  }),

  computed: {
    classes() {
      return classModule.classes;
    },
    usages() {
      return schoolServiceUsageModule.monthlyUsages;
    },
    schoolServices() {
      return schoolServiceModule.schoolServices.filter(s => s.isHidden !== 1 && s.serviceType !== 'perUse');
    },
    classesWithUsages(): ClassWithUsages[] {
      return this.classes.map((c) => {
        const usages = schoolServiceUsageModule.monthlyUsages.filter((u) => {
          const find = u.child.nurseryClasses.find((cls) => cls.classId === c.classId);
          if (find) {
            return true;
          }
          return false;
        }).map((u) => {
          const perUseServicesWithUsage = this.schoolServices.map((service) => {
            const findUsage = u.uniqueServices.find((s) => s.uniqueServiceId === service.uniqueServiceId);
            if (findUsage) {
              const registrationId = findUsage.registrations.length > 0 ? findUsage.registrations[0].registrationId : null;
              return {
                service,
                hasUsed: true,
                registrationId,
              }  
            }
            return {
              service,
              hasUsed: false,
            }
          });
          return {
            child: u.child,
            services: perUseServicesWithUsage
          }
        })
        .filter((u) => {
          if (u.child.quitDate) {
            const now = dayjs(this.yearMonth).endOf('month');
            const quitDate = dayjs(u.child.quitDate);

            const diff = now.diff(quitDate);
            if (diff > 0) {
              return false;
            }
          }
          return true;
        });

        return {
          class: c,
          usages,
        };
      });
    },
    tableWidth(): number {
      return 160 + (120 * this.schoolServices.length);
    }
  },

  watch: {
    yearMonth: {
      handler: function() {
        this.listUsages();
      },
    }
  },

  methods: {
    async pageClick(pageNumber: string) {
      if (pageNumber) sessionStorage.setItem('scoolServiceMonthlyPage', String(pageNumber));
    },
    async listUsages() {
      this.isFetching = true;
      const date = dayjs(this.yearMonth).startOf('month').format('YYYY-MM-DD');
      await schoolServiceUsageModule.listMonthlyUsages({ date });
      this.isFetching = false;
    },
    async toggleUsage(usage: PerUseServiceWithUsage, child: NurseryChildResponse) {
      if (this.isFetching) {
        return;
      }

      const date = dayjs(this.yearMonth).startOf('month').format('YYYY-MM-DD');

      if (!usage.hasUsed) {
        this.isFetching = true;
        await schoolServiceUsageModule.registerUsage({
          childId: child.childId,
          uniqueServiceId: usage.service.uniqueServiceId,
          date,
        });
        this.isFetching = false;
      } else {
        if (usage.registrationId) {
          this.isFetching = true;
          await schoolServiceUsageModule.deleteUsage({
            childId: child.childId,
            registrationId: usage.registrationId,
          });
          this.isFetching = false;
        }
      }
    },
  },

  mounted() {
    const scoolServiceMonthlyPage = sessionStorage.getItem('scoolServiceMonthlyPage');
    if (scoolServiceMonthlyPage) this.yearMonth = scoolServiceMonthlyPage;
    this.listUsages();
  }
});
</script>

<style lang="scss" scoped>
.class-block__header {
  background-color: #f0f0f0;
  padding: 0 8px;
  margin: 16px 0;
  display: flex;
  align-items: center;
}

.usages-headers {
  display: flex;
  align-items: center;
  height: 45px;
  min-width: max-content;
  border-bottom: solid 1px #e0e0e0;
  position: sticky;
  top: 0;
  background-color: #ffffff;
  z-index: 4;
}

.usages-header {
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 1px;
  min-height: 45px;
}

.usages-row {
  display: flex;
  align-items: center;
  height: 40px;
  margin-top: 8px;
  min-width: max-content;

  &:hover {
    background-color: #f5f5f5;
  }
}

.usages-item {
  font-size: 14px;
  width: 120px;
  padding: 8px;
  z-index: 1;

  &.col-item {
    cursor: pointer;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      background-color: #eaeaea;
    }
  }
}

.col-name {
  width: 160px;
  min-width: 160px; /* 追加: 潰れないように保証 */
  padding: 0 4px;
  position: sticky;
  left: 0; /* start ではなく left に統一 */
  background-color: #ffffff;
  z-index: 3;
}

.col-item {
  width: 120px;
  min-width: 120px; /* 追加: 潰れないように保証 */
  padding: 8px;
  text-align: center;
  font-size: 10px;
  word-break: break-all;
  z-index: 0;
}

.table-container {
  overflow-x: auto; /* visible ではなく auto に（はみ出たらスクロール） */
  overflow-y: auto;
  position: relative;
  max-height: 800px;
}
</style>